import React, { useEffect, useMemo, useRef } from 'react';
import { useJobNotifications } from '../contexts/JobNotificationsContext';
import { formatRelativeTime } from '../utils/dateUtils';
import { usePortfolioContext } from '../contexts/PortfolioContext';

const NOTIFICATION_CONFIG = {
  import_review: {
    label: 'Import Ready for Review',
    icon: '📋',
    color: 'text-blue-600 dark:text-blue-400',
    pathPrefix: '/imports/review/',
  },
  import_done: {
    label: 'Import Completed',
    icon: '✅',
    color: 'text-green-600 dark:text-green-400',
    pathPrefix: '/imports/result/',
  },
  import_failed: {
    label: 'Import Failed',
    icon: '❌',
    color: 'text-red-600 dark:text-red-400',
    pathPrefix: '/imports/result/',
  },
  export_done: {
    label: 'Export Ready',
    icon: '📥',
    color: 'text-green-600 dark:text-green-400',
    pathPrefix: '/imports',
    noJobIdSuffix: true,
  },
  export_failed: {
    label: 'Export Failed',
    icon: '❌',
    color: 'text-red-600 dark:text-red-400',
    pathPrefix: '/imports',
    noJobIdSuffix: true,
  },
};

const NotificationsBell = () => {
  const { currentPortfolioId } = usePortfolioContext();
  const { 
    notificationsByPortfolio, 
    removeNotification, 
    clearAll, 
    markPortfolioSeen, 
    getUnreadCount,
    navigateToJob,
  } = useJobNotifications();
  const detailsRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      const el = detailsRef.current;
      if (!el) return;
      if (el.open && !el.contains(e.target)) el.open = false;
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const items = useMemo(() => {
    const pf = currentPortfolioId;
    const list = notificationsByPortfolio[pf] || [];
    return [...list]
      .map((n) => ({ ...n, portfolioId: n.portfolioId || pf }))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 10);
  }, [notificationsByPortfolio, currentPortfolioId]);

  const unreadForCurrent = useMemo(() => getUnreadCount(currentPortfolioId), [getUnreadCount, currentPortfolioId]);

  const handleNotificationClick = (notification) => {
    const config = NOTIFICATION_CONFIG[notification.type];
    if (!config || !notification.jobId) return;

    // Close the dropdown
    if (detailsRef.current) detailsRef.current.open = false;

    // Use portfolio-aware navigation
    const targetPath = notification.targetPath || (config.noJobIdSuffix
      ? config.pathPrefix
      : `${config.pathPrefix}${notification.jobId}`);
    navigateToJob(notification.jobId, notification.portfolioId, targetPath);
  };

  if (!currentPortfolioId || items.length === 0) return null;

  return (
    <div className="relative">
      <details ref={detailsRef} onToggle={(e) => { if (e.currentTarget.open) markPortfolioSeen(currentPortfolioId); }}>
        <summary className="list-none cursor-pointer flex items-center gap-2 px-2 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
          <span className="relative inline-flex">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V4a2 2 0 10-4 0v1.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadForCurrent > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] rounded-full bg-blue-600 text-white">
                {unreadForCurrent}
              </span>
            )}
          </span>
        </summary>
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
          <div className="p-2 max-h-80 overflow-y-auto">
            {items.map((n) => {
              const config = NOTIFICATION_CONFIG[n.type] || {
                label: n.type,
                icon: '🔔',
                color: 'text-gray-600 dark:text-gray-400',
              };
              return (
                <div
                  key={`${n.id}-${n.type}`}
                  className="group flex items-center justify-between gap-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{config.icon}</span>
                    <div className="text-sm">
                      <div className={`font-medium ${config.color}`}>
                        {config.label}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {formatRelativeTime(n.createdAt)}
                      </div>
                      {n.message && (n.type === 'import_failed' || n.type === 'export_failed') && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-0.5 truncate max-w-[200px]">
                          {n.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    className="opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNotification(n.portfolioId, n.id);
                    }}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
          {items.length > 1 && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-2">
              <button
                className="w-full text-xs px-3 py-2 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  if ((notificationsByPortfolio[currentPortfolioId] || []).length > 0) clearAll(currentPortfolioId);
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

export default NotificationsBell;
